import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faLink } from '@fortawesome/free-solid-svg-icons';

import StockModal from './StockModal';

/**
 * Add or edit a catalogue item.
 *
 * THE LINKS ARE THE REASON THIS FORM EXISTS. Everything else here — name,
 * unit, supplier — is bookkeeping; the three link rows are what turn "we need
 * bin bags" into a purchase a manager can make in two clicks at 6am. The first
 * row is labelled as the one we usually buy from, the other two as fallbacks,
 * because a link with no rank is a link somebody has to think about.
 *
 * DELIVERY TIME IS PROSE ON PURPOSE. "2–4 working days", "same day if ordered
 * before 2pm", "3 weeks by sea" — a date picker here would either lose that or
 * force a fake precision. It is typed as it was told to us, and every request
 * for this item starts from it.
 */

const BLANK_LINK = { label: '', url: '', price: '' };

export default function ItemModal({ item, categories, defaultCategoryId, onClose, onSubmit }) {
    const editing = !!item;

    const [form, setForm] = useState(() => ({
        name: item?.name || '',
        category_id: item?.category_id || defaultCategoryId || categories[0]?.id || '',
        delivery_estimate: item?.delivery_estimate || '',
    }));

    const [links, setLinks] = useState(() => {
        const existing = (item?.links || [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((l) => ({ label: l.label || '', url: l.url, price: l.price ?? '' }));
        // Always one empty row to type into, up to the three the API accepts.
        return existing.length ? existing : [{ ...BLANK_LINK }];
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const setLink = (idx, key, value) => setLinks((ls) => ls.map(
        (l, i) => (i === idx ? { ...l, [key]: value } : l)
    ));

    const addLink = () => setLinks((ls) => (ls.length >= 3 ? ls : [...ls, { ...BLANK_LINK }]));
    const removeLink = (idx) => setLinks((ls) => (
        ls.length === 1 ? [{ ...BLANK_LINK }] : ls.filter((_, i) => i !== idx)
    ));

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!form.name.trim()) { setError('Give the item a name.'); return; }
        if (!form.category_id) { setError('Pick a category.'); return; }

        setSaving(true);
        setError('');
        try {
            await onSubmit({
                id: item?.id,
                ...form,
                name: form.name.trim(),
                category_id: Number(form.category_id),
                // Empty rows are simply not sent — the API treats a link with no
                // URL as "this box was left blank", not as an error.
                links: links
                    .filter((l) => l.url.trim())
                    .map((l) => ({
                        label: l.label.trim() || null,
                        url: l.url.trim(),
                        price: l.price === '' ? null : Number(l.price),
                    })),
            });
        } catch (err) {
            setError(err.response?.data?.error || 'That did not save. Try again.');
            setSaving(false);
        }
    };

    return (
        <StockModal
            title={editing ? `Edit — ${item.name}` : 'Add an item to the catalogue'}
            subtitle="Saved once, requested from then on by anyone in the warehouse."
            onClose={onClose}
            width={620}
        >
            <form onSubmit={submit}>
                <div className="stk__fieldRow">
                    <label className="stk__field stk__field--wide">
                        <span className="stk__fieldLabel">Item name</span>
                        <input
                            type="text"
                            className="stk__input"
                            value={form.name}
                            onChange={set('name')}
                            placeholder="e.g. Black bin bags, heavy duty"
                            maxLength={120}
                        />
                    </label>

                    <label className="stk__field">
                        <span className="stk__fieldLabel">Category</span>
                        <select className="stk__input" value={form.category_id} onChange={set('category_id')}>
                            {categories.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </label>
                </div>

                <label className="stk__field">
                    <span className="stk__fieldLabel">Approximate delivery time</span>
                    <input
                        type="text"
                        className="stk__input"
                        value={form.delivery_estimate}
                        onChange={set('delivery_estimate')}
                        placeholder="e.g. 2–4 working days"
                        maxLength={80}
                    />
                    <span className="stk__fieldHint">
                        Plain words — a range is fine. It prefills every order of this item,
                        and the warehouse sees it while they wait.
                    </span>
                </label>

                <div className="stk__field">
                    <span className="stk__fieldLabel">
                        <FontAwesomeIcon icon={faLink} /> Where to buy it
                    </span>
                    <span className="stk__fieldHint stk__fieldHint--above">
                        The first is the one we normally use; the other two are what you open
                        when it is out of stock. The shop name is what the board shows, and the
                        price is optional — a rough figure just helps you spot when one has moved.
                    </span>

                    <div className="stk__linkRows">
                        {links.map((l, idx) => (
                            <div key={idx} className="stk__linkRow">
                                <span className="stk__linkRank">
                                    {idx === 0 ? 'Usual' : `Alt ${idx}`}
                                </span>
                                <div className="stk__linkRowFields">
                                    <input
                                        type="text"
                                        className="stk__input"
                                        value={l.url}
                                        onChange={(e) => setLink(idx, 'url', e.target.value)}
                                        placeholder="https://…"
                                    />
                                    <div className="stk__linkRowMeta">
                                        <input
                                            type="text"
                                            className="stk__input"
                                            value={l.label}
                                            onChange={(e) => setLink(idx, 'label', e.target.value)}
                                            placeholder="Shop name (optional)"
                                            maxLength={80}
                                        />
                                        <input
                                            type="number"
                                            className="stk__input stk__input--price"
                                            value={l.price}
                                            onChange={(e) => setLink(idx, 'price', e.target.value)}
                                            placeholder="Price AED"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="stk__iconBtn stk__iconBtn--danger"
                                    onClick={() => removeLink(idx)}
                                    title="Remove this link"
                                    aria-label="Remove this link"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        ))}
                    </div>

                    {links.length < 3 && (
                        <button type="button" className="stk__linkish stk__linkish--add" onClick={addLink}>
                            + Add {links.length === 1 ? 'an alternative' : 'another alternative'}
                        </button>
                    )}
                </div>

                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>Close</button>
                    <button type="submit" className="stk__btn stk__btn--primary" disabled={saving}>
                        {saving ? 'Saving…' : (editing ? 'Save item' : 'Add item')}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}
